<script setup lang="ts">
import { computed, ref } from 'vue'

import type { CapabilityProfile, CapabilitySet } from '../types/callables'
import type { Capability } from '../types/sketcher'
import BaseDialog from '../ui/components/BaseDialog.vue'
import BaseField from '../ui/components/BaseField.vue'
import { useDialog } from '../ui/dialogs'

export interface EditProfilePayload {
  id?:          string
  name:         string
  description:  string
  capabilities: CapabilitySet
}

const props = defineProps<{
  /** undefined = create mode; present = edit mode. */
  profile?: CapabilityProfile
  save:     (payload: EditProfilePayload) => Promise<boolean>
}>()

const { close } = useDialog<EditProfilePayload>()

const isEdit = computed(() => Boolean(props.profile))

/**
 * Runtime vocabulary. Read off window.SketcherEngine.CAPABILITY (the IIFE
 * global set by the <script> tag in index.html) so a capability added in the
 * engine appears here on the next sync without a host edit. Typed via the
 * vendor .d.ts, so vue-tsc catches a rename or removal at build time.
 *
 * Descriptions are host-authored, since the engine has no place to put them
 * on the public surface. An unknown id (new engine capability that this file
 * hasn't been taught about) still appears — with no description, admin sees
 * the raw id and knows something new arrived. Better than hiding it and
 * silently locking new features away from every existing profile.
 */
const DESCRIPTIONS: Partial<Record<Capability, string>> = {
  'structure.disabled': 'Wall subsystem absent — no walls, no wall-based areas.',
  'structure.lock':     'Walls visible but read-only.',
  'arc.disabled':       'Arcs cannot be created or edited.',
  'ellipse.disabled':   'Ellipses cannot be created or edited.',
  'image.disabled':     'Images cannot be inserted or edited. Blocks insertImage() too.',
  'dimension.disabled': 'Dimensions (linear and angular) cannot be created or edited.',
  'callout.disabled':   'Callout tool disabled.',
  'leader.disabled':    'Leaders cannot be created or edited.',
  'layers.disabled':    'Layer management disabled and Layers panel hidden.',
  'pages.disabled':     'Page management disabled and Pages panel hidden.',
}

const KNOWN_CAPABILITIES: Array<{ id: Capability; description: string }> =
  Object.values(window.SketcherEngine.CAPABILITY).map((id) => ({
    id,
    description: DESCRIPTIONS[id] ?? '',
  }))

// Initialize form state from the profile prop at first setup. denyList default
// in create mode matches the engine's forward-compat posture (unknown/new
// capability IDs stay allowed).
const name        = ref(props.profile?.name ?? '')
const description = ref(props.profile?.description ?? '')
const mode        = ref<'denyList' | 'allowList'>(props.profile?.capabilities.mode ?? 'denyList')
const selected    = ref<Set<Capability>>(new Set(props.profile?.capabilities.list ?? []))

const busy       = ref(false)
const localError = ref<string | null>(null)

function toggleCapability(id: Capability, on: boolean) {
  const next = new Set(selected.value)
  if (on) next.add(id)
  else next.delete(id)
  selected.value = next
}

async function onSubmit() {
  const cleanName = name.value.trim()
  if (!cleanName) {
    localError.value = 'Name is required.'
    return
  }
  const payload: EditProfilePayload = {
    ...(isEdit.value ? { id: props.profile!.id } : {}),
    name:         cleanName,
    description:  description.value.trim(),
    capabilities: {
      mode: mode.value,
      list: [...selected.value],
    },
  }
  busy.value = true
  try {
    if (await props.save(payload)) close(payload)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <BaseDialog :title="isEdit ? 'Edit profile' : 'New profile'" size="wide" @dismiss="close()">
    <form id="edit-profile-form" @submit.prevent="onSubmit">
      <BaseField label="Name">
        <template #default="{ id }">
          <input :id="id" v-model="name" type="text" required :disabled="busy" autocomplete="off" class="input" />
        </template>
      </BaseField>

      <BaseField label="Description" hint="Optional">
        <template #default="{ id }">
          <input :id="id" v-model="description" type="text" :disabled="busy" autocomplete="off" class="input" />
        </template>
      </BaseField>

      <fieldset :disabled="busy">
        <legend>Mode</legend>
        <label class="check-row">
          <input type="radio" value="denyList" v-model="mode" class="radio" />
          <span>Deny-list — allow everything except the capabilities checked below</span>
        </label>
        <label class="check-row">
          <input type="radio" value="allowList" v-model="mode" class="radio" />
          <span>Allow-list — deny everything except the capabilities checked below</span>
        </label>
      </fieldset>

      <fieldset :disabled="busy">
        <legend>Capabilities</legend>
        <label v-for="cap in KNOWN_CAPABILITIES" :key="cap.id" class="check-row">
          <input
            type="checkbox"
            class="checkbox"
            :checked="selected.has(cap.id)"
            @change="toggleCapability(cap.id, ($event.target as HTMLInputElement).checked)"
          />
          <span><code>{{ cap.id }}</code> <em v-if="cap.description">{{ cap.description }}</em></span>
        </label>
        <p v-if="!KNOWN_CAPABILITIES.length" class="empty">
          The engine reports no capabilities. Re-run <code>npm run sync:sketcher</code>.
        </p>
      </fieldset>

      <p v-if="localError" class="field-error" role="alert">{{ localError }}</p>
    </form>

    <template #actions>
      <button type="button" class="btn btn-secondary" :disabled="busy" @click="close()">
        Cancel
      </button>
      <button type="submit" form="edit-profile-form" class="btn btn-primary" :disabled="busy">
        {{ busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create profile') }}
      </button>
    </template>
  </BaseDialog>
</template>

<style scoped>
form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

/* Row layout for a checkbox/radio + its label + optional description. Unique
   to this dialog's dense list of capabilities. */
.check-row {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  font-size: 0.85rem;
  line-height: 1.4;
  cursor: pointer;
}

.check-row input {
  margin-top: 0.18rem;
}

.check-row em {
  font-style: normal;
  opacity: 0.7;
  font-weight: 400;
}

code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  padding: 0 0.2rem;
  background: var(--bg-subtle);
  border-radius: 3px;
}

.empty {
  margin: var(--sp-1) 0 0;
  font-size: 0.8rem;
  color: var(--fg-muted);
}
</style>
